import React from "react";
import * as Sentry from "@sentry/react";
import type {Integration as SentryIntegration} from "@sentry/core";
import {useLocation, useNavigationType, createRoutesFromChildren, matchRoutes} from "react-router-dom";

import {Account, RegisteredUser} from "platformed-client/types";
import {domainType, getFrontendEnvironment, getPublicCredentials} from "./environment";
import {
  devNoiseIgnorePatterns,
  isExternalDomMutationNoise,
  isForbiddenRecordFetch,
  isHiddenTabImportFailure,
  isOfflineImportFailure,
  isThirdPartyInstrumentationNoise,
  isViteConsoleNoise,
} from "./sentryNoiseFilters";
import {hasBrokenErrorSubclassing} from "./brokenEnvironment";
import {tolgeeBackendPrefix} from "../App/i18n/backendPrefix";
import {applyEventGrouping, isRouteNotFound} from "./sentryGrouping";
import type {PostHog} from "posthog-js";
import {UNMASK_SELECTOR} from "platformed-components/Unmasked";

let posthogPromise: Promise<(typeof import("posthog-js"))["default"] | null> | null = null;

// Headless crawlers (search bots, link unfurlers, scrapers) routinely fail to
// run the app and emit errors no user will ever see — most visibly failed
// dynamic-import fetches. Detect them so their events can be dropped rather than
// inflating real issues.
function isProbablyBot(): boolean {
  if (typeof navigator === "undefined") return false;
  if (navigator.webdriver) return true;
  return /bot|crawl|spider|slurp|headless|bingbot|yandex|duckduckbot|baiduspider|facebookexternalhit|embedly|prerender/i.test(
    navigator.userAgent,
  );
}

declare global {
  interface Window {
    SentryClient: ReturnType<typeof Sentry.init>;
  }
}

function getPosthogConfig(): Parameters<PostHog["init"]> | null {
  const {posthog_host, posthog_ui_host, posthog_app_key, posthog_trust_center_key} = getPublicCredentials();

  const defaultConfig = {
    api_host: posthog_host,
    ui_host: posthog_ui_host,
    capture_pageview: false,
    disable_external_dependency_loading: true,
    mask_all_text: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      maskTextFn: (text: string, element?: HTMLElement) =>
        element?.closest(UNMASK_SELECTOR) ? text : text.replace(/\S/g, "*"),
      blockSelector: "img, picture, video, canvas, iframe, object, embed",
    },
  };

  switch (domainType) {
    case "TrustCenter":
      return posthog_trust_center_key ? [posthog_trust_center_key, defaultConfig] : null;
    case "Collect":
      return posthog_app_key ? [posthog_app_key, defaultConfig] : null;
    default:
      return posthog_app_key ? [posthog_app_key, defaultConfig] : null;
  }
}

export function analyticsInit() {
  try {
    const {environment, release} = getFrontendEnvironment();
    const {sentry_dsn} = getPublicCredentials();

    window.addEventListener("error", e => {
      if (e.error?._isNominal) {
        e.preventDefault();
      }
    });

    if (sentry_dsn) {
      // The prefix is a path on the page's own origin in deployed environments
      // and an absolute CDN URL elsewhere; resolving it covers both.
      const recordBaseUrl = new URL(tolgeeBackendPrefix, window.location.origin).href;
      const integrations: SentryIntegration[] = [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
          shouldCreateSpanForRequest(url) {
            // Avoid creating spans for download requests, as they cause
            // spurious "Large HTTP payload" errors in Sentry.
            return !url.endsWith("/download");
          },
        }),
        Sentry.captureConsoleIntegration({levels: ["error"]}),
        Sentry.replayIntegration({
          blockAllMedia: true,
          maskAllInputs: true,
          maskAllText: true,
          unmask: [UNMASK_SELECTOR],
        }),
      ];
      window.SentryClient = Sentry.init({
        dsn: sentry_dsn,
        environment,
        release: release ?? undefined,
        integrations,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: ["Non-Error promise rejection captured", ...devNoiseIgnorePatterns],
        beforeSend(event, hint) {
          // A page-context script (typically a managed-Chrome security extension)
          // has broken `extends Error`, so the app's instanceof/_isNominal error
          // handling can't classify anything here — its reports aren't actionable.
          if (hasBrokenErrorSubclassing()) {
            return null;
          }
          // Don't report "nominal" exceptions
          const originalException = hint.originalException as {_isNominal?: boolean} | undefined;
          if (originalException?._isNominal) {
            return null;
          }
          // Drop crawler traffic — it can't run the app and only emits noise.
          if (isProbablyBot()) {
            return null;
          }
          // Drop a translation-record fetch an intermediary answered with 403.
          if (isForbiddenRecordFetch(event, recordBaseUrl)) {
            return null;
          }
          // Drop errors thrown entirely inside an injected third-party script
          // (e.g. a browser extension patching setTimeout), which reach us
          // through Sentry's native-API instrumentation but never touch our code.
          if (isThirdPartyInstrumentationNoise(event, window.location.origin)) {
            return null;
          }
          // Drop React commit-phase removeChild/insertBefore failures caused by a
          // browser translation feature mutating the DOM out from under React.
          if (isExternalDomMutationNoise(event)) {
            return null;
          }
          // Drop failed Vite dev-server HMR fetches, which reach captureConsole
          // as a `[vite]`-tagged exception the ignore patterns can't see.
          if (isViteConsoleNoise(event)) {
            return null;
          }
          // Drop dynamic-import fetches abandoned by navigating away — an
          // import failure captured while the tab is hidden, which no user saw.
          const visibilityState = document.visibilityState;
          if (isHiddenTabImportFailure(event, visibilityState)) {
            return null;
          }
          // Drop dynamic-import fetches that failed while the browser was
          // offline — the request had no connectivity, on any tab.
          if (isOfflineImportFailure(event, navigator.onLine)) {
            return null;
          }
          // Drop react-router 404s — dead links (stale bookmarks, mistyped URLs,
          // renamed paths), not app faults.
          if (isRouteNotFound(event, hint)) {
            return null;
          }
          applyEventGrouping(event, hint, environment);
          // Page visibility at capture time. A request rejected because the
          // document is being torn down and one rejected by a transport failure
          // both surface as `TypeError: Failed to fetch`, with nothing in the
          // error to tell them apart; visibility is what separates them.
          event.tags = {...event.tags, visibility_state: visibilityState};
          return event;
        },
      });
    }

    const posthogConfig = getPosthogConfig();

    if (posthogConfig != null) {
      posthogPromise = import("./posthogInit")
        .then(({initPosthog}) => initPosthog(...posthogConfig))
        .catch((ex: unknown) => {
          // PostHog's chunk is frequently blocked by tracker/content blockers and
          // corporate web filters, surfacing as "Failed to fetch dynamically
          // imported module". Analytics is non-essential, so swallow the failure
          // rather than let it escalate to an unhandled rejection.
          Sentry.addBreadcrumb({
            category: "analytics",
            level: "warning",
            message: `PostHog failed to load: ${String(ex)}`,
          });
          return null;
        });
    }

    // Sentry reports are dropped for these clients (see `beforeSend`); this keeps
    // a count of how prevalent the broken environment is.
    if (hasBrokenErrorSubclassing()) {
      posthogPromise?.then(posthog => posthog?.capture("broken_js_environment"));
    }

    // Add our console.error interceptor after Sentry so that we can prevent non-errors getting to sentry.
    const originalError = console["error"];
    console["error"] = (...args) => {
      if (args[0] === "React Router caught the following error during render" && args[1]?._isNominal) {
        // Suppress stupid react errors
        return;
      } else if (args[0]?._isNominal) {
        return;
      }
      originalError(...args);
    };
  } catch (ex) {
    // Don't interrupt whatever the caller was doing
    console.error(ex);
  }
}

export function analyticsIdentifyUser({user_id, primary_email, name}: RegisteredUser) {
  Sentry.setUser({id: user_id, email: primary_email, username: name});
  posthogPromise?.then(posthog => posthog?.identify(user_id, undefined, {email: primary_email, name}));
}

export function analyticsIdentifyAccount(account: Account) {
  Sentry.setTags({
    "account.id": account.account_id,
    "account.name": account.display_name,
    "account.type": account.type_,
  });

  posthogPromise?.then(posthog =>
    posthog?.group("account", account.account_id, {accountName: account.display_name, accountType: account.type_}),
  );
}
